/**
 * OnboardingWizard — primera vez que un usuario entra en la CMDB.
 *
 * Dos pasos: idioma y aspecto. Se muestra DESPUÉS de autenticar (cuando ya
 * sabemos si el usuario tiene preferencia guardada) y ENCIMA de la app, así
 * que los cambios de tema se ven en vivo detrás del diálogo.
 *
 * El idioma se aplica al instante para que el propio asistente se lea en el
 * idioma elegido. Hasta pulsar «Empezar» no se guarda nada en el servidor:
 * `App.jsx` es quien persiste idioma + aspecto al recibir `onComplete`.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  applyAppearance,
  readStoredAppearance,
  saveAppearance,
} from '../theme/appearance';
import { SKINS, DEFAULT_SKIN } from '../theme/skins';
import { PALETTES, DEFAULT_PALETTE } from '../theme/palettes';
import { changeLanguage, currentLanguage, fetchRemoteLanguages } from '../i18n/config';
import {
  BUILTIN_LANGUAGES,
  detectBrowserLanguage,
  languageBadge,
  mergeLanguages,
  normalizeLanguageCode,
} from '../i18n/languages';
import { getUserDisplayName } from '../utils/userDisplay';
import './OnboardingWizard.css';

function SkinThumb({ skin }) {
  const preview = skin.preview;
  return (
    <span
      className="ob-skin-thumb"
      style={{
        background: preview.page,
        borderRadius: `${Math.min(14, preview.radius)}px`,
      }}
      aria-hidden="true"
    >
      <span
        className="ob-skin-thumb__side"
        style={{ background: preview.card, borderColor: preview.border }}
      />
      <span className="ob-skin-thumb__main">
        <span
          className="ob-skin-thumb__card"
          style={{
            background: preview.card,
            borderColor: preview.border,
            borderRadius: `${Math.max(4, Math.round(preview.radius * 0.55))}px`,
          }}
        >
          <span className="ob-skin-thumb__line" style={{ background: preview.text }} />
          <span className="ob-skin-thumb__line ob-skin-thumb__line--soft" style={{ background: preview.muted }} />
        </span>
        <span
          className="ob-skin-thumb__dot"
          style={{
            background: preview.glow
              ? 'linear-gradient(135deg, var(--accent), var(--accent-2))'
              : 'var(--accent)',
          }}
        />
      </span>
    </span>
  );
}

function PaletteThumb({ palette }) {
  return (
    <span className="ob-palette-thumb" style={{ background: palette.bg }} aria-hidden="true">
      <span className="ob-palette-thumb__side" style={{ background: palette.surface }} />
      <span className="ob-palette-thumb__main">
        <span className="ob-palette-thumb__bar" style={{ background: palette.surface }} />
        <span className="ob-palette-thumb__btn" style={{ background: palette.accent }} />
      </span>
    </span>
  );
}

function OnboardingWizard({ user, companyId, companyTheme = null, saving = false, onComplete }) {
  const { t } = useTranslation();
  const stored = useMemo(() => readStoredAppearance(), []);
  const serverUser = companyTheme?.user || null;
  const companyDefault = companyTheme?.company || null;

  const [step, setStep] = useState('language');
  const [languages, setLanguages] = useState(BUILTIN_LANGUAGES);
  const [language, setLanguage] = useState(() => currentLanguage() || detectBrowserLanguage());
  const [skin, setSkin] = useState(
    stored.skin || serverUser?.skin || companyDefault?.skin || DEFAULT_SKIN
  );
  const [palette, setPalette] = useState(
    stored.palette || serverUser?.palette || companyDefault?.palette || DEFAULT_PALETTE
  );
  const [localSaving, setLocalSaving] = useState(false);
  const [error, setError] = useState('');

  // Refs de la elección: si el usuario pulsa idioma → estilo → color → terminar
  // muy rápido, los estados de React aún no se han actualizado y se guardaría
  // la elección anterior. Los refs se actualizan en el mismo clic.
  const languageRef = useRef(language);
  const skinRef = useRef(skin);
  const paletteRef = useRef(palette);

  useEffect(() => { languageRef.current = language; }, [language]);
  useEffect(() => { skinRef.current = skin; }, [skin]);
  useEffect(() => { paletteRef.current = palette; }, [palette]);

  const busy = saving || localSaving;
  const displayName = useMemo(() => getUserDisplayName(user), [user]);

  useEffect(() => {
    let cancelled = false;
    fetchRemoteLanguages().then((remote) => {
      if (!cancelled) setLanguages(mergeLanguages(remote));
    });
    return () => { cancelled = true; };
  }, []);

  const uiLanguage = currentLanguage();

  const secondaryName = (lang) => {
    if (uiLanguage === 'en') return lang.nameEn;
    if (uiLanguage === 'ca') return lang.nameEs;
    return lang.nameEs;
  };

  const selectLanguage = async (code) => {
    const normalized = normalizeLanguageCode(code) || 'es';
    languageRef.current = normalized;
    setLanguage(normalized);
    await changeLanguage(normalized, { persist: false });
  };

  const selectSkin = (id) => {
    skinRef.current = id;
    setSkin(id);
    applyAppearance({ skin: id, palette: paletteRef.current });
  };

  const selectPalette = (id) => {
    paletteRef.current = id;
    setPalette(id);
    applyAppearance({ skin: skinRef.current, palette: id });
  };

  const finish = async () => {
    const finalSkin = skinRef.current;
    const finalPalette = paletteRef.current;
    const finalLanguage = languageRef.current;
    setError('');
    setLocalSaving(true);
    try {
      saveAppearance({ skin: finalSkin, palette: finalPalette });
      await onComplete({
        language: finalLanguage,
        skin: finalSkin,
        palette: finalPalette,
        themeChosen: true,
      });
    } catch {
      setError(t('onboarding.save_error', 'No se pudo guardar la elección. Inténtalo de nuevo.'));
    } finally {
      setLocalSaving(false);
    }
  };

  const skip = async () => {
    setError('');
    setLocalSaving(true);
    try {
      await onComplete({
        language: languageRef.current || detectBrowserLanguage(),
        skin: null,
        palette: null,
        themeChosen: false,
      });
    } catch {
      setError(t('onboarding.save_error', 'No se pudo guardar la elección. Inténtalo de nuevo.'));
    } finally {
      setLocalSaving(false);
    }
  };

  return (
    <div className="ob-overlay" role="dialog" aria-modal="true" aria-labelledby="ob-title">
      <div className="ob-card">
        <header className="ob-head">
          <div className="ob-head-text">
            <span className="ob-eyebrow">{t('onboarding.eyebrow', 'Bienvenido a la CMDB')}</span>
            <h1 className="ob-title" id="ob-title">
              {displayName
                ? t('onboarding.welcome_user', 'Hola, {{name}}', { name: displayName })
                : t('onboarding.title', 'Vamos a dejarlo a tu gusto')}
            </h1>
            <p className="ob-subtitle">
              {t(
                'onboarding.subtitle',
                'Elige el idioma y el aspecto. Puedes cambiarlo cuando quieras desde tu perfil.'
              )}
            </p>
          </div>
          <div className="ob-steps" aria-hidden="true">
            <span className={`ob-step ${step === 'language' ? 'is-active' : 'is-done'}`}>1</span>
            <span className="ob-step-line" />
            <span className={`ob-step ${step === 'theme' ? 'is-active' : ''}`}>2</span>
          </div>
        </header>

        {step === 'language' ? (
          <section className="ob-section">
            <div className="ob-section-head">
              <h2 className="ob-section-title">
                {t('onboarding.step_language', '1 · Idioma')}
              </h2>
              <p className="ob-section-hint">
                {t(
                  'onboarding.language_hint',
                  'La interfaz se traducirá al idioma que elijas.'
                )}
              </p>
            </div>

            <div className="ob-languages">
              {languages.map((lang) => {
                const active = language === lang.code;
                const secondary = secondaryName(lang);
                return (
                  <button
                    key={lang.code}
                    type="button"
                    className={`ob-language ${active ? 'is-active' : ''}`}
                    onClick={() => selectLanguage(lang.code)}
                    aria-pressed={active}
                  >
                    <span className="ob-language-badge">{languageBadge(lang.code)}</span>
                    <span className="ob-language-text">
                      <span className="ob-language-name">{lang.nativeName || lang.name}</span>
                      {secondary && secondary !== (lang.nativeName || lang.name) && (
                        <span className="ob-language-sub">{secondary}</span>
                      )}
                    </span>
                    {active && <span className="ob-language-check">✓</span>}
                  </button>
                );
              })}
            </div>

            <p className="ob-detected">
              {t('onboarding.detected', 'Idioma detectado en tu navegador')}:{' '}
              <strong>{languageBadge(detectBrowserLanguage())}</strong>
            </p>
          </section>
        ) : (
          <section className="ob-section">
            <div className="ob-section-head">
              <h2 className="ob-section-title">{t('onboarding.step_theme', '2 · Aspecto')}</h2>
              <p className="ob-section-hint">
                {t(
                  'onboarding.theme_hint',
                  'Se aplica al momento. Puedes probar sin miedo: no se guarda hasta que termines.'
                )}
              </p>
            </div>

            <div className="ob-theme-block">
              <span className="ob-theme-label">{t('onboarding.style', 'Estilo')}</span>
              <div className="ob-skins">
                {SKINS.map((item) => {
                  const active = skin === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`ob-skin ${active ? 'is-active' : ''}`}
                      onClick={() => selectSkin(item.id)}
                      title={t(`skins.${item.id}.description`, item.description)}
                      aria-pressed={active}
                    >
                      <SkinThumb skin={item} />
                      <span className="ob-skin-name">{item.name}</span>
                      <span className="ob-skin-tagline">
                        {t(`skins.${item.id}.tagline`, item.tagline)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="ob-theme-block">
              <span className="ob-theme-label">{t('onboarding.color', 'Color')}</span>
              <div className="ob-palettes">
                {PALETTES.map((item) => {
                  const active = palette === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`ob-palette ${active ? 'is-active' : ''}`}
                      onClick={() => selectPalette(item.id)}
                      title={item.name}
                      aria-pressed={active}
                    >
                      <PaletteThumb palette={item} />
                      <span className="ob-palette-name">{item.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {error && <div className="ob-error" role="alert">{error}</div>}

        <footer className="ob-footer">
          {step === 'language' ? (
            <button type="button" className="ob-link" onClick={skip} disabled={busy}>
              {t('onboarding.skip', 'Ahora no')}
            </button>
          ) : (
            <button
              type="button"
              className="ob-link"
              onClick={() => setStep('language')}
              disabled={busy}
            >
              ← {t('onboarding.back', 'Atrás')}
            </button>
          )}

          <div className="ob-footer-right">
            {step === 'language' && (
              <button
                type="button"
                className="ob-primary"
                onClick={() => setStep('theme')}
                disabled={busy || !language}
              >
                {t('onboarding.next', 'Continuar')} →
              </button>
            )}
            {step === 'theme' && (
              <button type="button" className="ob-primary" onClick={finish} disabled={busy}>
                {busy
                  ? t('onboarding.saving', 'Guardando…')
                  : t('onboarding.finish', 'Empezar a usar la CMDB')}
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}

export default OnboardingWizard;
